import { InAppState } from "../base/InAppState";
import template from "./RegionTemplate.html";

import { Region } from "../../core/Region";
import { MapView } from "../../core/MapView";
import { DragScroll } from "../../core/DragScroll";
import { _lcs } from "../../i18n";

// Region key -> display label (platform-defined regions). German source
// strings double as their dictionary keys; render() translates them.
const REGION_LABELS = {
    saechsische_schweiz : "Sächsische Schweiz"
};

/**
 * RegionState — the "Entdecken" tab: restaurants, sights and routes around the
 * house, as a list or on a map. The class, the state id and the route are all
 * still "region"; only the label a guest reads changed.
 *
 * The data is real (core/Region.js → the public place/tour endpoints) and it
 * arrives NEAREST FIRST, which is what makes a bounded list honest: the pool
 * behind this screen holds thousands of records across the whole region, and
 * the sixty a guest sees are the sixty they could actually reach.
 *
 * The category is re-rendered on every switch (each is its own feed and its own
 * scroll), the view toggle is not: list and map are the same records, so the
 * map is built once, on first use, and only re-framed afterwards.
 */
export class RegionState extends InAppState
{
    static ROUTE = "/region";
    static ID = "region";

    getActiveTab() { return "region"; }

    async enter()
    {
        this._category = RegionState.lastCategory || Region.CATEGORIES[ 0 ].id;
        this._view = "list";
        this._map = null;
        this._selected = null;

        // Paint the chips and whatever is already cached before waiting on the
        // network — coming back to the tab should be instant.
        this.render();

        if ( !this.app.region.state( this._category ).loaded )
        {
            await this.app.region.load( this._category );
            this.render();
        }
    }

    async exit()
    {
        this._destroyMap();
        await super.exit();
    }

    render()
    {
        const cards = this.app.region.getCards( this._category );

        this.renderScreen( template, {
            region     : _lcs( REGION_LABELS[ this.app.accommodation?.region ] ||
                         this.app.accommodation?.region || "Region" ),
            categories : Region.CATEGORIES,
            active     : this._category,
            cards      : cards
        } );

        this._paintState( cards );
        this.bind();
    }

    /**
     * The line above the list. Four different situations, four different
     * sentences — an empty list with no explanation reads as "there is nothing
     * here", which is only one of them.
     */
    _paintState( cards )
    {
        const root = this.app.view.pageContent;
        const count = root.querySelector( '[data-id="count"]' );
        const state = root.querySelector( '[data-id="state"]' );
        const status = this.app.region.state( this._category );

        count.textContent = cards.length > 0
            ? _lcs( Region.category( this._category ).countKey, {
                count : cards.length, n : cards.length
            } )
            : "";

        if ( cards.length > 0 )
        {
            state.hidden = true;
            return;
        }

        if ( this.app.region.isDemo() )
        {
            state.textContent = _lcs( "Diese Unterkunft ist noch nicht freigeschaltet — " +
                "Empfehlungen aus der Region erscheinen hier, sobald sie es ist." );
        }
        else if ( status.failed )
        {
            state.textContent = _lcs( "Die Empfehlungen konnten nicht geladen werden. " +
                "Bitte versuche es später noch einmal." );
        }
        else if ( !status.loaded )
        {
            state.textContent = _lcs( "Wird geladen …" );
        }
        else
        {
            state.textContent = _lcs( "Für diese Kategorie gibt es hier noch keine Einträge." );
        }

        state.hidden = false;
    }

    bind()
    {
        const root = this.app.view.pageContent;

        // "Sehenswürdigkeiten" alone is wider than a third of a phone, so the
        // chip row always overflows — which on a desktop leaves the last chip
        // unreachable without this (see core/DragScroll.js).
        const chips = root.querySelector( ".region__chips" );
        DragScroll.attach( this, chips );
        // Every category switch re-renders the row from scratch, so the active
        // chip would otherwise sit off-screen behind the right edge.
        DragScroll.reveal( chips, chips.querySelector( ".chip--active" ) );

        root.querySelectorAll( "[data-category]" ).forEach( ( chip ) =>
        {
            this.addEventListener( chip, "click", () =>
                this._switchCategory( chip.getAttribute( "data-category" ) ) );
        } );

        root.querySelectorAll( "[data-view]" ).forEach( ( btn ) =>
        {
            this.addEventListener( btn, "click", () => this._switchView( btn.getAttribute( "data-view" ) ) );
        } );

        root.querySelectorAll( ".regioncard" ).forEach( ( card ) =>
        {
            this.addEventListener( card, "click", () => this._open( card.getAttribute( "data-entry" ) ) );
        } );

        // Bound now, resolved later: the map is torn down and rebuilt whenever
        // the category changes, so the buttons must not hold on to one.
        MapView.bindControls( this, root, () => this._map );

        const mapCard = root.querySelector( '[data-id="map-card"]' );
        this.addEventListener( mapCard, "click", () =>
        {
            if ( this._selected )
            {
                this._open( this._selected.kind + ":" + this._selected.id );
            }
        } );
    }

    /* ------------------------------- Navigation ------------------------------ */

    _open( entry )
    {
        const [ kind, id ] = String( entry || "" ).split( ":" );
        if ( kind && id )
        {
            this.app.router.redirect( "/region/" + kind + "/" + id );
        }
    }

    async _switchCategory( categoryId )
    {
        if ( categoryId === this._category )
        {
            return;
        }

        this._destroyMap();
        this._category = categoryId;
        this._selected = null;
        // Remembered for the next visit: a guest who came for restaurants is
        // usually still after restaurants.
        RegionState.lastCategory = categoryId;

        const keepMap = this._view === "map";
        this._view = "list";
        this.render();

        if ( !this.app.region.state( categoryId ).loaded )
        {
            await this.app.region.load( categoryId );
            // Another chip may have been tapped while this was in flight.
            if ( this.app.stateManager.currentState !== this || this._category !== categoryId )
            {
                return;
            }
            this.render();
        }

        if ( keepMap )
        {
            this._switchView( "map" );
        }
    }

    _switchView( view )
    {
        if ( view === this._view )
        {
            return;
        }
        this._view = view;

        const root = this.app.view.pageContent;

        root.querySelectorAll( "[data-view]" ).forEach( ( btn ) =>
            btn.classList.toggle( "viewswitch__btn--on", btn.getAttribute( "data-view" ) === view ) );

        root.querySelector( '[data-id="list"]' ).hidden = view !== "list";
        root.querySelector( '[data-id="map"]' ).hidden = view !== "map";

        if ( view === "map" )
        {
            this._showMap();
        }
    }

    /* ---------------------------------- Map ---------------------------------- */

    /**
     * Build the map, or re-measure the one that is already there.
     *
     * Called only from _switchView, i.e. only once the container is visible:
     * Leaflet measures on creation and a container inside a `hidden` element is
     * 0×0, after which the tiles are laid out against the wrong size for good.
     */
    _showMap()
    {
        const root = this.app.view.pageContent;
        const canvas = root.querySelector( '[data-id="map-canvas"]' );
        const hint = root.querySelector( '[data-id="map-hint"]' );

        if ( this._map )
        {
            // The container was hidden while the list was up, so its size is
            // stale even though the map is not.
            this._map.invalidate();
            this._map.reset();
            return;
        }

        if ( !MapView.isAvailable( this.app ) )
        {
            // Say why there is no map instead of leaving a grey rectangle.
            hint.textContent = _lcs( "Für diese Unterkunft ist keine Kartenquelle hinterlegt." );
            hint.hidden = false;
            return;
        }

        const cards = this.app.region.getCards( this._category ).filter( ( c ) => c.latitude !== null );

        try
        {
            this._map = new MapView( this.app, canvas );

            const house = this.app.accommodation?.address;
            if ( house )
            {
                this._map.addHere( house.latitude, house.longitude,
                    this.app.accommodation?.name || null );
            }

            cards.forEach( ( card ) => this._map.addPin( card, ( picked ) => this._select( picked ) ) );
            this._map.fit();

            if ( cards.length === 0 )
            {
                hint.textContent = _lcs( "Zu diesen Einträgen sind keine Koordinaten hinterlegt." );
                hint.hidden = false;
            }

            // The frame is only final once the browser has laid the container
            // out at its real size.
            requestAnimationFrame( () =>
            {
                if ( this._map )
                {
                    this._map.invalidate();
                    this._map.fit();
                }
            } );
        }
        catch ( e )
        {
            console.error( e );
            this._destroyMap();
            hint.textContent = _lcs( "Die Karte konnte nicht geladen werden." );
            hint.hidden = false;
        }
    }

    /** Tapping a pin raises its card over the map; tapping it again drops it. */
    _select( card )
    {
        const root = this.app.view.pageContent;
        const panel = root.querySelector( '[data-id="map-card"]' );

        if ( this._selected && this._selected.id === card.id )
        {
            this._selected = null;
            panel.hidden = true;
            this._map.setSelected( null );
            return;
        }

        this._selected = card;

        const media = root.querySelector( '[data-id="map-card-media"]' );
        media.className = "mapcard__media ga-photo ga-photo--" + card.photo;
        media.style.backgroundImage = card.image ? 'url("' + card.image + '")' : "";

        root.querySelector( '[data-id="map-card-cat"]' ).textContent = card.cat;
        root.querySelector( '[data-id="map-card-title"]' ).textContent = card.title;
        root.querySelector( '[data-id="map-card-meta"]' ).textContent =
            [ card.distance, card.meta ].filter( Boolean ).join( " · " );

        panel.hidden = false;
        this._map.setSelected( card.id );
    }

    _destroyMap()
    {
        if ( this._map )
        {
            this._map.destroy();
            this._map = null;
        }
    }
}
